// Keeps an open patient record in step with the patient's own verification, off the back of the
// PushEventService poll.
//
// This one is unlike the others in where the change comes from. Every other push event is raised by
// another staff member; this one is raised by the patient, following the link texted or emailed to
// them, at a moment nobody in the practice is doing anything. Without it the record on screen goes
// on showing them as unverified until someone reloads it - and the person most likely to be looking
// is the one who just asked them to verify.
//
// Only the two verify dates are copied across. The record on screen may be half-edited, and the
// patient confirming their mobile is no reason to move a name or an address the user is part-way
// through typing - so the whole object is deliberately not replaced.
app.service("PatientVerificationLiveUpdateService", [
	"PatientAPI",
	"LiveReloadService",
	"PUSH_EVENTS",
	"LiveUpdateGuard",
	function (PatientAPI, LiveReloadService, PUSH_EVENTS, LiveUpdateGuard) {
		// The fields a verification can change. Named as the patient JSON carries them, which is
		// snake_case for these two even though most of that payload is camelCase.
		const VERIFY_FIELDS = ["email_verify_date", "mobile_number_verify_date"];

		/**
		 * Wires a view up to patient verification events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  patient() - the patient record on screen, patched in place; falsy when none is
		 *                  open, which drops the event without a request
		 *              onChanged() - re-render after a patch
		 */
		function subscribe(scope, spec) {
			// Patient ids with a fetch in flight. A verification publishes to both the practice channel
			// and the channel of the staff member who sent it, so that person receives two events with
			// different ids - the poll's own de-duplication does not catch them.
			const fetching = {};

			/** The open patient, or null when the view has none. */
			function openPatient() {
				const patient = spec.patient ? spec.patient() : null;
				return patient && patient.id ? patient : null;
			}

			/** Copies the verify dates onto the open record. Returns whether anything changed. */
			function applyVerifyDates(patient, fetched) {
				let changed = false;
				VERIFY_FIELDS.forEach(function (field) {
					if (patient[field] !== fetched[field]) {
						patient[field] = fetched[field];
						changed = true;
					}
				});
				return changed;
			}

			function onVerified(payload) {
				const patient = openPatient();
				if (!patient || !payload.patientId || !LiveReloadService.sameId(patient.id, payload.patientId)) {
					return;
				}
				if (fetching[payload.patientId]) {
					return;
				}
				fetching[payload.patientId] = true;

				// Through LiveUpdateGuard, which stops this after a few failures in a row.
				LiveUpdateGuard.run("patientVerification", function () {
					return PatientAPI.getPatient(patient.id);
				}).then(
					function (resp) {
						delete fetching[payload.patientId];

						// Re-read: the user may have moved to another patient while this was in flight, and
						// writing these dates onto a different record would say the wrong thing about them.
						const current = openPatient();
						if (!current || !LiveReloadService.sameId(current.id, payload.patientId) || !resp.data) {
							return;
						}

						if (applyVerifyDates(current, resp.data) && spec.onChanged) {
							spec.onChanged();
						}
					},
					function () {
						// Deliberately silent: the poll will not re-announce this, so the record stays as it
						// is until the next reload. Nothing is logged - the response is a patient record.
						delete fetching[payload.patientId];
					}
				);
			}

			scope.$on(PUSH_EVENTS.PATIENT_VERIFICATION_COMPLETED, function (e, pushEvent) {
				onVerified((pushEvent && pushEvent.data) || {});
			});
		}

		const service = {
			subscribe: subscribe,
		};

		return service;
	},
]);
